/** Portable research only. No clearing, normalization, or listing authority occurs here. */
/** Finish/Rarity are research; 'Card Name' is the reviewed English card name for a non-English card (Latin script only,
 * since eBay.com titles and specifics are searched in English). Owner 2026-10-20. */
export type BatchTransferItemSpecificName = 'Finish' | 'Rarity' | 'Card Name';
export const BATCH_TRANSFER_ITEM_SPECIFIC_NAMES: readonly BatchTransferItemSpecificName[] = [
  'Finish',
  'Rarity',
  'Card Name',
];
export type BatchTransferItemSpecifics = Partial<Record<BatchTransferItemSpecificName, string>>;
export const BATCH_TRANSFER_ITEM_SPECIFICS_MAX_BYTES = 1024;
export const BATCH_TRANSFER_ITEM_SPECIFIC_MAX_LENGTH = 200;
// eslint-disable-next-line no-control-regex -- The package contract forbids C0/C1 controls.
const controls = /[\u0000-\u001f\u007f-\u009f]/;
// Latin script (Basic Latin through Latin Extended-B) plus typographic quotes and the ellipsis.
const nonLatin = /[^\u0000-ɏ‘-‟…]/;
const trimWhitespace =
  '\\u0009-\\u000d\\u0020\\u00a0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000\\ufeff';
const valueSchema = {
  type: 'string',
  minLength: 1,
  maxLength: BATCH_TRANSFER_ITEM_SPECIFIC_MAX_LENGTH,
  pattern: `[^${trimWhitespace}]`,
  not: { pattern: '[\\u0000-\\u001f\\u007f-\\u009f|]' },
};
/** Standard JSON Schema cannot express aggregate encoded UTF8 bytes or UTF16 units.
 * The semantic parser additionally enforces 1KiB JSON serialization and 200 UTF16 units. */
export const BATCH_TRANSFER_ITEM_SPECIFICS_SCHEMA = {
  type: 'object',
  minProperties: 1,
  maxProperties: 3,
  additionalProperties: false,
  description:
    'Research only. Semantic server validation also enforces <=200 UTF-16 units per value and <=1024 UTF-8 bytes of JSON serialization; JSON Schema maxLength counts code points.',
  properties: {
    Finish: valueSchema,
    Rarity: valueSchema,
    'Card Name': {
      ...valueSchema,
      not: {
        pattern: '[\\u0000-\\u001f\\u007f-\\u009f|]|[^\\u0000-\\u024f\\u2018-\\u201f\\u2026]',
      },
    },
  },
};
export function parseBatchTransferItemSpecifics(
  value: unknown,
  path = 'listing.itemSpecifics',
): { ok: true; value: BatchTransferItemSpecifics } | { ok: false; error: string } {
  const invalid = (field?: BatchTransferItemSpecificName) => ({
    ok: false as const,
    error: `Invalid ${path}${field === undefined ? '' : `.${field}`}.`,
  });
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return invalid();
  const v = value as Record<string, unknown>;
  const keys = Object.keys(v);
  if (
    keys.length < 1 ||
    keys.length > BATCH_TRANSFER_ITEM_SPECIFIC_NAMES.length ||
    keys.some((k) => !(BATCH_TRANSFER_ITEM_SPECIFIC_NAMES as readonly string[]).includes(k))
  )
    return invalid();
  const result: BatchTransferItemSpecifics = {};
  for (const key of keys as BatchTransferItemSpecificName[]) {
    const entry = v[key];
    if (
      typeof entry !== 'string' ||
      entry.trim() === '' ||
      entry.length > BATCH_TRANSFER_ITEM_SPECIFIC_MAX_LENGTH ||
      controls.test(entry) ||
      entry.includes('|') ||
      (key === 'Card Name' && nonLatin.test(entry))
    )
      return invalid(key);
    result[key] = entry;
  }
  if (
    new TextEncoder().encode(JSON.stringify(result)).length >
    BATCH_TRANSFER_ITEM_SPECIFICS_MAX_BYTES
  )
    return invalid();
  return { ok: true, value: result };
}
